import { useEffect, useState } from 'react';
import { api } from '../api';
import type { Assignment, Chapter } from '../types';
import { renderMd } from '../md';

export function Assignments({ chapters }: { chapters: Chapter[] }) {
  const [items, setItems] = useState<Assignment[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expand, setExpand] = useState<number | null>(null);

  const reload = () => api.listAssignments().then(setItems);
  useEffect(() => {
    reload();
  }, []);

  const create = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setBusy(true);
    try {
      await api.createAssignment({
        title: String(fd.get('title')),
        chapter_id: fd.get('chapter_id') ? Number(fd.get('chapter_id')) : null,
        prompt_md: String(fd.get('prompt_md') ?? ''),
        due_date: (fd.get('due_date') as string) || null,
        status: 'todo',
      });
      e.currentTarget.reset();
      setOpen(false);
      reload();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const cycleStatus = async (a: Assignment) => {
    const next: Assignment['status'] =
      a.status === 'todo'
        ? 'in_progress'
        : a.status === 'in_progress'
          ? 'done'
          : 'todo';
    await api.updateAssignment(a.id, { status: next });
    reload();
  };

  return (
    <div>
      <div className="flex-between">
        <h2 style={{ margin: 0 }}>Assignments</h2>
        <button className="btn" onClick={() => setOpen((v) => !v)}>
          {open ? 'Close' : '＋ New assignment'}
        </button>
      </div>

      {open && (
        <form onSubmit={create} className="card col">
          <input name="title" placeholder="Assignment title" required />
          <select name="chapter_id" defaultValue="">
            <option value="">— No chapter —</option>
            {chapters.map((c) => (
              <option key={c.id} value={c.id}>
                Ch.{c.number} {c.title}
              </option>
            ))}
          </select>
          <input type="date" name="due_date" />
          <textarea name="prompt_md" placeholder="Prompt / problem statement (markdown)" />
          <div>
            <button className="btn" disabled={busy}>
              {busy ? 'Saving…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {items.map((a) => {
        const chapter = chapters.find((c) => c.id === a.chapter_id);
        return (
          <div key={a.id} className="card">
            <div className="flex-between">
              <div>
                <span
                  className={`pill ${a.status}`}
                  onClick={() => cycleStatus(a)}
                  style={{ cursor: 'pointer' }}
                  title="Click to cycle status"
                >
                  {a.status}
                </span>{' '}
                <strong>{a.title}</strong>
                {chapter && <span className="muted"> · Ch.{chapter.number}</span>}
                {a.due_date && (
                  <span className="muted"> · due {a.due_date}</span>
                )}
              </div>
              <div className="row">
                <button
                  className="btn secondary"
                  onClick={() => setExpand(expand === a.id ? null : a.id)}
                >
                  {expand === a.id ? 'Hide' : 'View'}
                </button>
                <button
                  className="btn danger"
                  onClick={async () => {
                    if (!confirm('Delete assignment?')) return;
                    await api.deleteAssignment(a.id);
                    reload();
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
            {expand === a.id && (
              <AssignmentEditor assignment={a} onSave={reload} />
            )}
          </div>
        );
      })}
      {!items.length && <div className="muted">No assignments yet.</div>}
    </div>
  );
}

function AssignmentEditor({
  assignment,
  onSave,
}: {
  assignment: Assignment;
  onSave: () => void;
}) {
  const [prompt, setPrompt] = useState(assignment.prompt_md ?? '');
  const [solution, setSolution] = useState(assignment.solution_md ?? '');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.updateAssignment(assignment.id, {
        prompt_md: prompt,
        solution_md: solution,
      });
      setEditing(false);
      onSave();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      {!editing ? (
        <>
          <h4>Prompt</h4>
          <div
            className="markdown"
            dangerouslySetInnerHTML={{
              __html: renderMd(prompt) || '<p class="muted">No prompt.</p>',
            }}
          />
          <h4>Solution</h4>
          <div
            className="markdown"
            dangerouslySetInnerHTML={{
              __html: renderMd(solution) || '<p class="muted">No solution yet.</p>',
            }}
          />
          <button
            className="btn secondary"
            onClick={() => setEditing(true)}
            style={{ marginTop: 8 }}
          >
            Edit
          </button>
        </>
      ) : (
        <>
          <h4>Prompt (markdown)</h4>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} />
          <h4>Solution (markdown)</h4>
          <textarea
            value={solution}
            onChange={(e) => setSolution(e.target.value)}
          />
          <div className="row" style={{ marginTop: 8 }}>
            <button
              className="btn secondary"
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>
            <button className="btn" disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
